import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useMobile } from './MobileContext';
import { createCallSession, setSpeaker, playRemote } from '@/utils/webrtc';
import { PhoneCall, PhoneOff, Loader2, Mic, MicOff, AlertTriangle, Volume2, VolumeX } from 'lucide-react';

export const fmtDur = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

export const useCallTimer = (active) => {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    if (!active) { setSec(0); return; }
    const iv = setInterval(() => setSec(s => s + 1), 1000);
    return () => clearInterval(iv);
  }, [active]);
  return sec;
};

const ConfirmCall = ({ t, onYes, onNo, needInfo, guest }) => {
  const [name, setName] = useState(guest?.name || '');
  const [phone, setPhone] = useState(guest?.phone || '');
  const ok = !needInfo || (name.trim() && phone.trim());
  return (
  <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-5" data-testid="m-call-confirm">
    <div className="w-full max-w-sm rounded-3xl bg-white p-5 text-center shadow-2xl">
      <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto"><AlertTriangle className="w-7 h-7" /></div>
      <p className="mt-3 text-base font-bold text-slate-900">{t('تنبيه', 'Notice')}</p>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{t('سوف تقوم الآن بالتواصل مع الموظف المختص. هل أنت متأكد من الاستمرار؟', 'You are about to contact the specialist. Are you sure you want to continue?')}</p>
      {needInfo && <div className="mt-3 space-y-2 text-start">
        <input value={name} onChange={e => setName(e.target.value)} placeholder={t('اسمك *', 'Your name *')} className="w-full h-11 rounded-xl border border-slate-200 px-3 text-base" data-testid="m-call-name" />
        <input value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('رقم هاتفك *', 'Your phone *')} inputMode="tel" dir="ltr" className="w-full h-11 rounded-xl border border-slate-200 px-3 text-base" data-testid="m-call-phone" />
      </div>}
      <div className="flex gap-2 mt-4">
        <button onClick={() => ok && onYes({ name: name.trim(), phone: phone.trim() })} disabled={!ok} className="flex-1 h-11 rounded-xl bg-emerald-600 text-white text-sm font-bold disabled:opacity-40" data-testid="m-call-confirm-yes">{t('نعم، اتصل', 'Yes, call')}</button>
        <button onClick={onNo} className="flex-1 h-11 rounded-xl bg-slate-100 text-slate-700 text-sm font-bold" data-testid="m-call-confirm-no">{t('إلغاء', 'Cancel')}</button>
      </div>
    </div>
  </div>
  );
};

export const useVoiceCall = () => {
  const { API, headers, t, isClient, guest, updateGuest } = useMobile();
  const [status, setStatus] = useState('idle'); // idle | ringing | active | connected | reconnecting | ended
  const [confirm, setConfirm] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speaker, setSpk] = useState(false);
  const [needTap, setNeedTap] = useState(false);
  const [info, setInfo] = useState('');
  const streamRef = useRef(null);
  const sessRef = useRef(null);
  const audioRef = useRef(null);
  const inCall = ['active', 'connected', 'reconnecting'].includes(status);
  const sec = useCallTimer(inCall);
  const needInfo = !isClient && (!guest?.name || !guest?.phone);
  const ask = () => setConfirm(true);
  const start = async (info = {}) => {
    setConfirm(false); setMuted(false); setSpk(false); setNeedTap(false); setInfo('');
    const who = { name: info.name || guest?.name, phone: info.phone || guest?.phone };
    if (!isClient && info.name && (info.name !== guest?.name || info.phone !== guest?.phone)) updateGuest({ name: info.name, phone: info.phone }).catch(() => {});
    const a = audioRef.current; if (a) { a.muted = false; a.volume = 1; a.play().catch(() => {}); }
    try {
      const s = createCallSession({ api: API, headers, side: 'user', onInfo: setInfo,
        onRemoteStream: async (st) => { streamRef.current = st; const ok = await playRemote(audioRef.current, st, sessRef.current?.log); setNeedTap(!ok); setSpeaker(audioRef.current, false); },
        onStatus: (st) => { setStatus(st); if (st === 'ended') { setNeedTap(false); setTimeout(() => setStatus('idle'), 2500); } } });
      sessRef.current = s;
      await s.call(who);
    } catch (e) { toast.error(t('تعذّر الوصول إلى المايكروفون — اسمح للمتصفح باستخدام المايك ثم أعد المحاولة', 'Microphone access failed — allow the mic and retry')); setStatus('idle'); }
  };
  const tapToPlay = async () => { const ok = await playRemote(audioRef.current, streamRef.current, sessRef.current?.log); setNeedTap(!ok); };
  const hang = () => sessRef.current?.end(true);
  const toggleMute = () => { const m = !muted; sessRef.current?.setMuted(m); setMuted(m); };
  const toggleSpeaker = async () => {
    const on = !speaker; setSpk(on);
    const el = audioRef.current; if (el) el.play().catch(() => {});
    const ok = await setSpeaker(el, on);
    if (!ok) toast(t('جهازك يتحكم بمخرج الصوت — استخدم أزرار الصوت أو سماعة الهاتف', 'Your device controls audio output — use the volume buttons or your phone speaker'));
  };
  return { t, status, inCall, confirm, setConfirm, muted, speaker, sec, audioRef, ask, start, hang, toggleMute, toggleSpeaker, needTap, tapToPlay, info, needInfo, guest };
};

export const VoiceTile = ({ vc }) => {
  const { t, status, ask, inCall } = vc;
  const busy = status !== 'idle' && status !== 'ended';
  return (
    <button onClick={ask} disabled={busy} className="relative rounded-2xl border p-3 flex flex-col items-center gap-1.5 text-center bg-emerald-600 border-emerald-600 text-white disabled:opacity-70" data-testid="m-call-start">
      <span className="absolute -top-2 start-2 text-[9px] font-black bg-[#D4AF37] text-[#0F172A] px-2 py-0.5 rounded-full" data-testid="m-call-free-badge">{t('مجاني', 'FREE')}</span>
      {busy && !inCall ? <Loader2 className="w-5 h-5 animate-spin" /> : <PhoneCall className="w-5 h-5" />}
      <p className="text-xs font-bold leading-tight">{t('اتصال صوتي مجاني', 'Free voice call')}</p>
      <p className="text-[10px] text-white/80">{t('عبر الإنترنت', 'via internet')}</p>
    </button>
  );
};

export const VoicePanel = ({ vc }) => {
  const { t, status, inCall, confirm, setConfirm, muted, speaker, sec, audioRef, start, hang, toggleMute, toggleSpeaker, needTap, tapToPlay, info, needInfo, guest } = vc;
  const label = { ringing: t('جاري الاتصال... انتظر الرد', 'Calling... waiting for answer'), active: t('تم الرد — جارٍ توصيل الصوت...', 'Answered — connecting audio...'), connected: t('المكالمة جارية — ضع الهاتف على أذنك', 'Call in progress — hold the phone to your ear'), reconnecting: t('ضعف في الشبكة... إعادة الاتصال', 'Weak network... reconnecting'), ended: t('انتهت المكالمة', 'Call ended') }[status];
  const round = (on, onIcon, offIcon, onClick, tid, active) => <button onClick={onClick} className={`w-12 h-12 rounded-full flex items-center justify-center ${active ? 'bg-[#D4AF37] text-[#0F172A]' : 'bg-white/10 text-white'}`} data-testid={tid}>{on ? onIcon : offIcon}</button>;
  return (
    <>
      <audio ref={audioRef} autoPlay playsInline />
      {confirm && <ConfirmCall t={t} onYes={start} onNo={() => setConfirm(false)} needInfo={needInfo} guest={guest} />}
      {status !== 'idle' && (
        <div className="rounded-2xl bg-[#0F172A] text-white p-4" data-testid="m-voice-call">
          <div className="flex items-center gap-3">
            <span className={`w-3 h-3 rounded-full shrink-0 ${status === 'connected' ? 'bg-emerald-400 animate-pulse' : status === 'ended' ? 'bg-slate-400' : 'bg-amber-400 animate-pulse'}`} />
            <p className="flex-1 text-sm font-bold" data-testid="m-call-status">{label}</p>
            {inCall && <span className="text-sm font-mono text-[#D4AF37]" dir="ltr" data-testid="m-call-timer">{fmtDur(sec)}</span>}
          </div>
          {needTap && <button onClick={tapToPlay} className="w-full mt-3 h-11 rounded-xl bg-[#D4AF37] text-[#0F172A] text-sm font-bold flex items-center justify-center gap-2 animate-pulse" data-testid="m-call-tap-play"><Volume2 className="w-4 h-4" />{t('اضغط هنا لتشغيل الصوت', 'Tap to enable audio')}</button>}
          {status !== 'ended' && (
            <div className="flex items-center justify-center gap-4 mt-4">
              {inCall && round(muted, <MicOff className="w-5 h-5" />, <Mic className="w-5 h-5" />, toggleMute, 'm-call-mute', muted)}
              {inCall && round(speaker, <Volume2 className="w-5 h-5" />, <VolumeX className="w-5 h-5" />, toggleSpeaker, 'm-call-speaker', speaker)}
              <button onClick={hang} className="w-14 h-14 rounded-full bg-red-600 text-white flex items-center justify-center" data-testid="m-call-hangup"><PhoneOff className="w-6 h-6" /></button>
            </div>
          )}
          {inCall && <div className="flex justify-center gap-10 mt-1.5 text-[10px] text-white/60"><span>{muted ? t('المايك مكتوم', 'Muted') : t('كتم', 'Mute')}</span><span>{t('السماعة', 'Speaker')}</span><span>{t('إنهاء', 'End')}</span></div>}
          {info && status !== 'ended' && <p className="mt-2 text-[10px] text-white/40 text-center font-mono truncate" dir="ltr" data-testid="m-call-info">{info}</p>}
        </div>
      )}
    </>
  );
};
